import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import useForm from '../common/form/hooks/useForm';
import GenericHeader from '../common/pageHeader/GenericHeader';
import ReCaptcha from '../common/form/recaptcha/ReCaptcha';
import Button from '../common/button/Button';
import Input from '../common/form/Input';
import { methods } from '../common/form/constants/validator';
import { configs } from '../../constants/configs/configs';
import { INPUT_TYPES } from '../common/form/constants/fieldTypes';
import { VALIDATION_TYPES } from '../../constants/inputs/validationTypes';
import accessCodeExpandedView from './fragments/AccessCodeExpandedView';
import accessCodeNonExpandedView from './fragments/AccessCodeNonExpandedView';
import { useAccessCode } from './hooks/useAccessCode';
import { ErrorModal } from '../../components/errorModal/ErrorModal';
import { ConditionalRendering } from '../../components/common/conditional-rendering';

interface IAccessCodePageProps {
    environment: {
        exchangeName: string;
        location: {
            pathname: string;
        };
        accessCodeExpandedViewEnabled: boolean;
        hideCaptcha: boolean;
        properties: {
            RECAPTCHA_SITE_KEY: string;
        };
        stateCode: string;
        language: string;
    };
    authentication: boolean;
}

const AccessCodePage = (props: IAccessCodePageProps) => {
    const { states } = configs;
    const [errorMessage, setErrorMessage] = useState('');
    const {
        environment: {
            accessCodeExpandedViewEnabled,
            hideCaptcha,
            properties: { RECAPTCHA_SITE_KEY },
            stateCode,
            language,
        },
    } = props;

    const reCaptchaWrapperExpandedViewEnabledClass =
        'desktop:grid-col-8 tablet:grid-col-8 mobile:grid-col-8 grid-col-6 margin-tb-10';
    const reCaptchaWrapperNonExpandedViewEnabledClass =
        'desktop:grid-col-8 tablet:grid-col-8 mobile:grid-col-8 grid-col-6 margin-b-20';
    const reCaptchaWrapperClass = accessCodeExpandedViewEnabled
        ? reCaptchaWrapperExpandedViewEnabledClass
        : reCaptchaWrapperNonExpandedViewEnabledClass;
    const { handleChange, values, errors, handleBlur, handleFocus, resetFields } = useForm(
        {},
        {
            accessCodeSelection: 'accessCode',
            accessCode: '',
            ssn: '',
            captchaValue: '',
        },
        {
            accessCode: {
                type: 'maxLength',
                validator: value => !methods.maxLength([value, VALIDATION_TYPES.ACCESSCODE_MAX_LENGTH]),
            },
            ssn: [
                { type: 'ssn', validator: value => !methods.ssn(value) },
                { type: 'exactLength', validator: value => !methods.exactLength([value, VALIDATION_TYPES.SSN_LENGTH]) },
            ],
        },
    );

    const navigate = useNavigate();
    const { verifyAccessCode } = useAccessCode();

    useEffect(() => {
        resetFields(['accessCode']);
    }, [values && values.accessCodeSelection]);

    const formDisabled = (!values.ssn && !values.accessCode) || (!hideCaptcha && !values.captchaValue);

    const i18nReplacements = {
        accessCode: <FormattedMessage id="accounts.login_success.access_code" />,
        ssn: <FormattedMessage id="accounts.login_success.access_code" />,
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const target = e.target as HTMLFormElement;
        if (target?.ssn?.value) {
            target.ssn.value = values.ssn.replace(/-/g, '');
        }
        const result = await verifyAccessCode(values);
        if (!result) return;
        const { securityQuestions, encryptedActivationId, reference, errorMessage } = result;
        if (securityQuestions) {
            navigate(configs.routes.DYNAMIC_SECURITY_QUESTIONS, {
                state: { randomQuestions: securityQuestions, encryptedActivationId, reference },
            });
            return;
        }
        if (errorMessage) {
            setErrorMessage(errorMessage);
        }
    };

    return (
        <Fragment>
            <GenericHeader text="accounts.login_success.header_1" tag="h1" />
            <div className="grid-container padding-0">
                <div className="grid-row padding-0">
                    <div className="gi-panel desktop:grid-col-fill tablet:grid-col-fill mobile-lg:grid-col-12 grid-col-12">
                        <div className="desktop:grid-col-10 tablet:grid-col-10 mobile-lg:grid-col-12 grid-col-12 section__content">
                            <form name="access-form" onSubmit={handleSubmit}>
                                <ConditionalRendering isTrue={!accessCodeExpandedViewEnabled}>
                                    {accessCodeNonExpandedView({
                                        values,
                                        i18nReplacements,
                                        handleChange,
                                        handleBlur,
                                        handleFocus,
                                        errors,
                                    })}
                                </ConditionalRendering>
                                <ConditionalRendering isTrue={accessCodeExpandedViewEnabled}>
                                    {accessCodeExpandedView({
                                        values,
                                        i18nReplacements,
                                        handleChange,
                                        handleBlur,
                                        handleFocus,
                                        errors,
                                        stateCode,
                                    })}
                                </ConditionalRendering>
                                <ConditionalRendering isTrue={!hideCaptcha}>
                                    <ReCaptcha
                                        sitekey={RECAPTCHA_SITE_KEY}
                                        key={language}
                                        lang={language}
                                        setCaptchaValue={values.captchaValue}
                                        onValidated={() =>
                                            handleChange({ target: { name: 'captchaValue', value: true } })
                                        }
                                        outerClass={['center']}
                                        wrapperClass={[reCaptchaWrapperClass]}
                                    />
                                </ConditionalRendering>

                                <Input
                                    label={{
                                        text: 'common.no_text',
                                        isInputRequired: false,
                                        labelClasses: [''],
                                        wrapperClasses: [''],
                                    }}
                                    inputField={{
                                        name: 'csrftoken',
                                        inputClasses: [''],
                                        type: 'hidden',
                                        value: window.csrf_token,
                                    }}
                                />
                                <Button
                                    {...{
                                        id: 'submit',
                                        name: 'submit',
                                        type: 'submit',
                                        outerClass: ['margin-b-20'],
                                        classes: ['gi-button__primary'],
                                        link_type: INPUT_TYPES.SUBMIT,
                                        btnText: 'button.submit',
                                        disabled: formDisabled
                                    }}
                                />
                            </form>
                            <ConditionalRendering isTrue={stateCode === states.IDAHO}>
                                <FormattedMessage
                                    id="accounts.accessCode.id_help_text"
                                    values={{
                                        strong: chunks => {
                                            return <strong>{chunks}</strong>;
                                        },
                                    }}
                                />
                            </ConditionalRendering>
                        </div>
                    </div>
                    {/*<StartApplication />*/}
                </div>
            </div>
            <ConditionalRendering isTrue={errorMessage.length > 0}>
                <ErrorModal onClose={() => setErrorMessage('')} errorText={errorMessage} titleId={'common.modal.failure'} />
            </ConditionalRendering>
        </Fragment>
    );
};

export default AccessCodePage;
